import type { Company, ThemeClasses } from "../types";
import { brand, brandTextColor } from "../../../../../../config/theme";
import { CompanyActionsMenu } from "./CompanyActionsMenu";

interface CompanyGridCardProps {
  company: Company;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (company: Company) => void;
  onEdit: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onActivate?: (company: Company) => void;
  onSoftDelete?: (company: Company) => void;
  onSources?: (company: Company) => void;
  onRetransmissions?: (company: Company) => void;
  t: (key: string) => string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (company: Company) => void;
}

export function CompanyGridCard({
  company,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  onActivate,
  onSoftDelete,
  onSources,
  onRetransmissions,
  t,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
}: CompanyGridCardProps) {

  const getStatusColor = (status_id: string) => {
    // Handle empty string, null, undefined
    if (!status_id || status_id === "") {
      return "text-gray-500 bg-gray-500/10";
    }

    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq": // facturable
        return "facturable";
      case "Y2aD7bE1fF6jM3kO9hP5": // suspended
        return "text-red-500 bg-red-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  // const getStatusLabel = (status_id: string) => {
  const getStatusLabel = (status_id: string) => {
    // Handle empty string, null, undefined
    if (!status_id || status_id === "") {
      return "Desconocida";
    }

    switch (status_id) {
      case "jXmjzHcBD5E1J0wjwHzq": // facturable
        return "Activa";
      case "Y2aD7bE1fF6jM3kO9hP5": // suspended
        return "Suspendida";
      case "D4h7DGEqSEXZcG0ByStX": // deleted
        return "Eliminada";
      default:
        return "Desconocida";
    }
  };

  const handleCardClick = () => {
    if (isSelectionMode && onToggleSelection) {
      onToggleSelection(company);
    }
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all ${
        isSelectionMode ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-primary-500" : ""}`}
      onClick={isSelectionMode ? handleCardClick : undefined}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div className="flex items-center mb-3">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-primary-500 border-primary-500"
                : isDarkMode
                ? "border-gray-500"
                : "border-gray-300"
            }`}
          >
            {isSelected && <i className="ri-check-line text-white text-sm"></i>}
          </div>
          <span className={`ml-2 text-sm ${themeClasses.textSecondary}`}>
            {isSelected ? t("companies.selected") : t("companies.selectToAction")}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex items-center">
          {company.logo ? (
            <img
              src={company.logo}
              alt={company.name}
              className="w-12 h-12 rounded-xl object-cover"
            />
          ) : (
            <div
              className={`flex w-12 h-12 rounded-xl items-center justify-center ${
                isDarkMode ? "bg-[#4A4A4A]" : "bg-gray-100"
              }`}
            >
              <i
                className={`ri-building-2-line text-xl ${themeClasses.textSecondary}`}
              ></i>
            </div>
          )}
        </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text} line-clamp-1`}>
              {company.name}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary} line-clamp-1`}>
              {company.business_name}
            </p>
          </div>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
              getStatusColor(company?.status_id || "") === "facturable" ? "" : getStatusColor(company?.status_id || "")}`}
            style={getStatusColor(company?.status_id || "") === "facturable" ? { color: brandTextColor, backgroundColor: `${brand.primary}1A` } : undefined}
          >
            {getStatusLabel(company?.status_id || "")}
          </span>
      </div>

      {/* Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2">
          <i
            className={`ri-mail-line text-sm ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-sm ${themeClasses.textSecondary} truncate`}>
            {company.email}
          </span>
        </div>
        {company.phone && (
          <div className="flex items-center space-x-2">
            <i
              className={`ri-phone-line text-sm ${themeClasses.textSecondary}`}
            ></i>
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {company.phone}
            </span>
          </div>
        )}
        <div className="flex items-center space-x-2">
          <i
            className={`ri-map-pin-line text-sm ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-sm ${themeClasses.textSecondary} truncate`}>
            {company.contact_address?.city}, {company.contact_address?.state}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className={`pt-3 border-t ${themeClasses.cardBorder} space-y-2`}>
        <CompanyActionsMenu
          company={company}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onDetails={onDetails}
          onEdit={onEdit}
          onDelete={onDelete}
          onActivate={onActivate}
          onSoftDelete={onSoftDelete}
          onSources={onSources}
          onRetransmissions={onRetransmissions}
          fullWidth
        />
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          ID: {company.id}
        </p>
      </div>
    </div>
  );
}
